import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext();

// In production (single-port deployment), use relative paths for same-origin API calls.
// Only use REACT_APP_BACKEND_URL in development when frontend/backend run on different ports.
const getApiBaseUrl = () => {
  // In production (app.theocrm.com), use relative paths for same-origin API
  const hostname = window.location.hostname;
  if (hostname === "app.theocrm.com" || hostname === "theocrm.com") {
    return "";
  }
  // In development, use the configured backend URL
  return process.env.REACT_APP_BACKEND_URL || "";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("theo_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    
    // Handle 401 Unauthorized errors (invalid/expired sessions)
    if (status === 401) {
      // Clear token
      localStorage.removeItem("theo_token");
      const currentPath = window.location.pathname;

      // PUBLIC ROUTES ALLOWLIST - never auto-redirect to /login for these paths
      // These routes handle their own auth state
      const publicRoutes = [
        "/login",
        "/register",
        "/billing/subscribe",
        "/billing/return",
        "/invite/",        // Workspace invite accept page
        "/beta",           // Beta redemption page
        "/forgot-password",
        "/reset-password",
      ];
      
      const isPublicRoute = publicRoutes.some(route => 
        currentPath === route || currentPath.startsWith(route)
      );
      
      if (isPublicRoute) {
        return Promise.reject(error);
      }

      // For all other routes, redirect to login
      window.location.href = "/login?session_expired=1";
      return Promise.reject(error);
    }
    
    // Don't intercept 403 errors if we're on beta-related pages
    // This allows the beta redemption flow to work correctly
    const currentPath = window.location.pathname;
    const isBetaFlow = currentPath === "/beta" || currentPath === "/register" || currentPath === "/login";
    
    if (status === 403 && (detail === "BETA_INVITE_REQUIRED" || detail === "BETA_EXPIRED") && !isBetaFlow) {
      const target = detail === "BETA_INVITE_REQUIRED" ? "/beta-required" : "/beta-expired";
      window.location.href = target;
      return Promise.reject(error);
    }

    // Billing / subscription gating: show paywall for locked workspaces
    if (status === 402) {
      const code = detail?.code;
      const subscribeUrl = detail?.subscribe_url || "/app/billing/subscribe";
      const message = detail?.message || "Subscribe to continue using Theo.";
      
      // Handle all payment required codes (both lowercase and uppercase for backward compat)
      const paywallCodes = [
        "payment_required",
        "trial_expired",
        "subscription_canceled",
        "TRIAL_EXPIRED",
        "PAST_DUE",
        "SUBSCRIPTION_REQUIRED"
      ];
      
      if (paywallCodes.includes(code)) {
        // Store paywall info in sessionStorage for the PaywallScreen to pick up
        sessionStorage.setItem("theo_paywall", JSON.stringify({
          show: true,
          code,
          message,
          subscribe_url: subscribeUrl
        }));
        // Dispatch custom event so PaywallScreen can react
        window.dispatchEvent(new CustomEvent("theo:paywall", { detail: { code, message, subscribe_url: subscribeUrl } }));
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);


// Public routes where auth bootstrap should be skipped
const PUBLIC_ROUTES = [
  "/invite/",
  "/login",
  "/register",
  "/beta",
  "/forgot-password",
  "/reset-password",
  "/billing/subscribe",
  "/billing/return",
];

const isPublicRoute = () => {
  const path = window.location.pathname;
  return PUBLIC_ROUTES.some(route => path === route || path.startsWith(route));
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem("theo_token");
    
    // Skip auth bootstrap on public routes to prevent unwanted redirects
    // These routes handle their own auth state
    if (isPublicRoute()) {
      if (isMounted) setLoading(false);
      return () => { isMounted = false; };
    }
    
    if (!token) {
      if (isMounted) setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    Promise.all([api.get("/api/auth/me"), api.get("/api/account/entitlements")])
      .then(([meRes, entRes]) => {
        if (!isMounted) return;
        setUser(meRes.data);
        setEntitlements(entRes.data);
      })
      .catch(() => {
        localStorage.removeItem("theo_token");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/api/auth/login", { email, password });
    const token = res.data.access_token;
    localStorage.setItem("theo_token", token);
    
    // Try to fetch user data and entitlements
    // If they fail (e.g., due to beta gating), that's okay - the user object will be set after beta redemption
    try {
      const [me, ent] = await Promise.all([
        api.get("/api/auth/me"),
        api.get("/api/account/entitlements"),
      ]);
      setUser(me.data);
      setEntitlements(ent.data);
    } catch (err) {
      // If we can't fetch user data (e.g., beta gating), set minimal user object from JWT
      // The BetaPage will handle redemption and then the user will be fully loaded
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser({ email, id: payload.sub });
    }
  };

  const register = async (payload) => {
    await api.post("/api/auth/register", payload);
  };

  const logout = () => {
    localStorage.removeItem("theo_token");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, api, entitlements, setEntitlements }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
